// Date-range filter for the dashboard ticket list.
//
// Renders as a single chip on the filter bar. Clicking it opens a small
// dialog with quick presets ("Today", "7 days", "This month", …) plus
// a custom-range section with two date inputs. Apply commits the range
// to dashboard state; Clear removes the filter.
//
// Why a dialog (not an inline popover):
//   - We want the filter bar to stay compact at all viewport sizes.
//   - Popovers with form inputs are fiddly to get right re: focus trap +
//     click-outside dismissal across mobile + desktop. Dialog already
//     handles all of that via Radix.
//
// State model: this component is uncontrolled — it commits via `onApply`
// and `onClear`. The parent owns the actual `filters.createdFrom/To`
// and just renders the chip label off it.

import { useEffect, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { TICKET_DATE_FIELD_LABELS, type TicketDateField } from '@/lib/queries';

interface Props {
  /** ISO timestamps. Both optional — apply only the bounds that are set. */
  from: string | undefined;
  to: string | undefined;
  /** Which timestamp column the range targets. Defaults to `created_at`
   *  in the parent — this prop only carries the explicit selection. */
  dateField: TicketDateField | undefined;
  /** Called when the user picks a preset or clicks "Apply custom range".
   *  Always carries the (possibly unchanged) dateField so the parent
   *  doesn't have to merge two callbacks. */
  onApply: (next: {
    from: string | undefined;
    to: string | undefined;
    dateField: TicketDateField;
  }) => void;
  /** Called when the user clicks Clear (or the × on the active chip). */
  onClear: () => void;
}

const DEFAULT_FIELD: TicketDateField = 'created_at';

/** Friendly label for the chip — shows the active selection so the user
 *  can read what's applied without re-opening the picker. ALWAYS prefixes
 *  the field name (Created / Resolved / Updated / etc.) so the chip
 *  unambiguously communicates which timestamp column is being filtered.
 *  Previously hid the field name on the default (`created_at`) case,
 *  which made it impossible to tell at a glance whether "Today" meant
 *  "created today" or "resolved today" if a URL drill-down or a stray
 *  click on the field selector had changed the column. */
function describeRange(
  from: string | undefined,
  to: string | undefined,
  field: TicketDateField,
): string {
  if (!from && !to) return 'Date';
  const preset = matchPreset(from, to);
  const rangeLabel = preset
    ? preset.label
    : (() => {
        const fmt = (iso?: string) =>
          iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }) : '…';
        return `${fmt(from)} → ${fmt(to)}`;
      })();
  // Lower-case the field label inside the chip so the prefix reads like
  // a natural-language phrase ("created · today") not an awkward
  // capitalisation jolt ("Created · Today"). Keeps the chip short.
  return `${TICKET_DATE_FIELD_LABELS[field].toLowerCase()} · ${rangeLabel}`;
}

// ─── Preset definitions ──────────────────────────────────────────────────────
// Each preset resolves to a `{ from, to }` pair when applied. `to` is left
// undefined for "X days ago up to now" presets so the filter naturally
// includes new tickets opened during the day without re-applying.

interface Preset {
  key: string;
  label: string;
  compute: () => { from: string | undefined; to: string | undefined };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

const PRESETS: Preset[] = [
  {
    key: 'today',
    label: 'Today',
    // Closed range — start to end of today. Previously this preset
    // was open-ended (`to: undefined`), which (a) read as "today
    // onwards" rather than "today" and (b) silently let any future-
    // dated rows (clock-skew, scheduled tickets) match. Now matches
    // the shape of Yesterday — same calendar-day-as-a-closed-range
    // pattern — so "Today" means today and only today.
    compute: () => {
      const t = new Date();
      return { from: startOfDay(t).toISOString(), to: endOfDay(t).toISOString() };
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    compute: () => {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y).toISOString(), to: endOfDay(y).toISOString() };
    },
  },
  {
    key: '7d',
    label: 'Last 7 days',
    compute: () => {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      return { from: startOfDay(d).toISOString(), to: undefined };
    },
  },
  {
    key: '30d',
    label: 'Last 30 days',
    compute: () => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return { from: startOfDay(d).toISOString(), to: undefined };
    },
  },
  {
    key: '90d',
    label: 'Last 90 days',
    compute: () => {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      return { from: startOfDay(d).toISOString(), to: undefined };
    },
  },
  {
    key: 'thisMonth',
    label: 'This month',
    compute: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfDay(first).toISOString(), to: undefined };
    },
  },
  {
    key: 'lastMonth',
    label: 'Last month',
    compute: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(first).toISOString(), to: endOfDay(last).toISOString() };
    },
  },
  {
    key: 'thisQuarter',
    label: 'This quarter',
    compute: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const first = new Date(now.getFullYear(), q * 3, 1);
      return { from: startOfDay(first).toISOString(), to: undefined };
    },
  },
];

/** Best-effort reverse lookup: given a current range, did it come from a
 *  preset? Used to show "Last 7 days" rather than the raw date range on
 *  the chip when the bounds round-trip cleanly. Match is timestamp-equal
 *  (preset .compute() is deterministic up to seconds, so two presets
 *  computed within the same second give the same answer — fine). */
function matchPreset(from: string | undefined, to: string | undefined): Preset | null {
  for (const p of PRESETS) {
    const r = p.compute();
    if (r.from === from && r.to === to) return p;
  }
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DateRangeFilter({ from, to, dateField, onApply, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const activeField: TicketDateField = dateField ?? DEFAULT_FIELD;
  // Local copy of the field while the dialog is open — committed
  // alongside whatever range action the user takes (preset click or
  // custom apply). Resets to the parent value every time the dialog
  // opens so cancelled changes don't leak.
  const [draftField, setDraftField] = useState<TicketDateField>(activeField);
  // Custom-range form state. Initialised from the current selection so
  // re-opening the dialog after applying a custom range shows what was
  // picked.
  const [customFrom, setCustomFrom] = useState<string>(toDateInput(from));
  const [customTo, setCustomTo] = useState<string>(toDateInput(to));

  // Sync local form state when parent-driven range/field changes (e.g.
  // preset applied externally, or filter cleared via the × on the chip).
  useEffect(() => {
    setCustomFrom(toDateInput(from));
    setCustomTo(toDateInput(to));
    setDraftField(activeField);
  }, [from, to, activeField, open]);

  const isActive = !!(from || to);
  const chipLabel = describeRange(from, to, activeField);

  const applyPreset = (preset: Preset) => {
    onApply({ ...preset.compute(), dateField: draftField });
    setOpen(false);
  };

  const applyCustom = () => {
    // Either bound may be empty — that's an explicit "open-ended" choice.
    const fromIso = customFrom ? startOfDay(new Date(customFrom)).toISOString() : undefined;
    const toIso = customTo ? endOfDay(new Date(customTo)).toISOString() : undefined;
    if (fromIso && toIso && new Date(fromIso) > new Date(toIso)) {
      // Don't apply backwards ranges — user almost certainly meant the
      // opposite. Cheaper than a toast: just swap.
      onApply({ from: toIso, to: fromIso, dateField: draftField });
    } else {
      onApply({ from: fromIso, to: toIso, dateField: draftField });
    }
    setOpen(false);
  };

  // "Just change the field" — re-apply the SAME range against the new
  // column. Active only when there's a range to re-apply; otherwise the
  // field selector just stages the choice for the next preset/custom apply.
  const switchField = (next: TicketDateField) => {
    setDraftField(next);
    if (isActive) {
      onApply({ from, to, dateField: next });
      // Don't close — user often wants to compare same-range / different-
      // dimensions back-to-back ("How many tickets did we resolve this
      // week vs. how many came in?").
    }
  };

  return (
    <>
      {/* Chip — composite when active so the user can clear with one click
          without having to open the dialog. */}
      <div className="inline-flex items-stretch">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'inline-flex h-6 cursor-pointer items-center gap-1 rounded-md border px-2 text-2xs font-medium transition-colors',
            isActive
              ? 'border-transparent bg-tint-active text-tint-active-fg ring-1 ring-inset ring-tint-active-ring hover:bg-tint-active-ring'
              : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            isActive && 'rounded-r-none',
          )}
        >
          <Calendar className="h-3 w-3" />
          {chipLabel}
        </button>
        {isActive ? (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-6 cursor-pointer items-center rounded-r-md border border-l-0 border-transparent bg-tint-active px-1.5 text-tint-active-fg/80 ring-1 ring-inset ring-tint-active-ring transition-colors hover:bg-tint-active-ring hover:text-tint-active-fg"
            title="Clear date filter"
            aria-label="Clear date filter"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            {/* Title surfaces the live field choice so picking "Today"
                from the preset grid below unambiguously means "tickets
                {{field}} today" — eliminates the "I thought I filtered
                created-today but I actually filtered last-team-reply-
                today" confusion that bit users when the field was a
                hidden default. */}
            <DialogTitle>
              Filter by <span className="text-foreground/70">{TICKET_DATE_FIELD_LABELS[draftField]}</span> date
            </DialogTitle>
            <DialogDescription>
              Pick a preset range or enter a custom one. Tap a different
              column in the "Filter by" row to switch which timestamp
              the range targets.
            </DialogDescription>
          </DialogHeader>

          {/* Field picker — segmented control. Picks which timestamp
              column the range targets. Changing the field with an
              active range re-applies the same range against the new
              column immediately (lets you flip "created this week" →
              "resolved this week" without re-picking the range).
              Without an active range, the choice just stages for the
              next preset/custom apply. */}
          <div className="rounded-md border border-border/70 bg-muted/40 p-2.5">
            <p className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Filter by
            </p>
            {/* Reply-field clarifier: tells the user that the strict
                "last voice was this side" rule is applied — matches
                what the chip name implies. The dashboard layers a
                cross-column comparison on top of the server's
                date-range filter so e.g. "Client replied last" can't
                return a `waiting_on_client` ticket where the team
                spoke after the client. */}
            {(draftField === 'last_client_message_at' || draftField === 'last_team_message_at') ? (
              <p className="mt-1 text-3xs leading-snug text-muted-foreground">
                Shows tickets where {draftField === 'last_client_message_at' ? 'the client' : 'the team'} sent
                the LAST message in this range (the other side hasn't replied since).
                {draftField === 'last_client_message_at' ? ' Use this for "what needs my response right now".' : ' Use this for "what I\'m waiting on the client for".'}
              </p>
            ) : null}
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(Object.keys(TICKET_DATE_FIELD_LABELS) as TicketDateField[]).map((f) => {
                const isCurrent = draftField === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => switchField(f)}
                    className={cn(
                      'inline-flex h-7 cursor-pointer items-center rounded-md border px-2.5 text-2xs font-medium transition-colors',
                      isCurrent
                        ? 'border-transparent bg-foreground text-background'
                        : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {TICKET_DATE_FIELD_LABELS[f]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preset grid — 3 columns at most so labels stay readable on
              narrow viewports. Active preset is highlighted indigo so
              re-opening the dialog after an apply makes the current
              selection obvious. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {PRESETS.map((p) => {
              const matched = matchPreset(from, to);
              const isCurrent = matched?.key === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={cn(
                    'inline-flex h-8 items-center justify-center rounded-md border px-2 text-xs font-medium transition-colors',
                    isCurrent
                      ? 'border-tint-active-ring bg-tint-active text-tint-active-fg'
                      : 'border-border hover:border-foreground/40 hover:bg-accent',
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* When a preset is the active range, hide the Custom Range
              section behind a small "Or pick a custom range" toggle.
              Reasoning: leaving Custom Range visible when a preset is
              active double-shows the SAME date in the "From" input
              (auto-populated from the preset), which leads users to
              think they need to also click "Apply custom range" to
              actually apply Today. Hiding it when a preset matches
              makes the active mode unambiguous; the toggle keeps the
              custom path one click away when needed. */}
          {matchPreset(from, to) ? (
            <button
              type="button"
              onClick={() => onApply({ from: undefined, to: undefined, dateField: draftField })}
              className="cursor-pointer rounded-md border border-dashed border-border/70 px-3 py-2 text-left text-2xs text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
            >
              Need different dates? Tap here to clear the preset and pick a custom range.
            </button>
          ) : (
          <div className="mt-2 rounded-md border border-border/70 bg-muted/40 p-3">
            <p className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Custom range
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="date-from" className="text-2xs text-muted-foreground">
                  From
                </Label>
                <input
                  id="date-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="date-to" className="text-2xs text-muted-foreground">
                  To
                </Label>
                <input
                  id="date-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-sm"
                />
              </div>
            </div>
            <p className="mt-2 text-3xs text-muted-foreground">
              Leave one side empty for an open-ended range. From-dates start at 00:00; To-dates end at 23:59.
            </p>
          </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {isActive ? (
              <Button
                variant="ghost"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
              >
                Clear
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setOpen(false)}>
              {/* When a preset is the active mode, the explicit
                  "Apply custom range" button is hidden — so Cancel
                  becomes the only way to dismiss without changes.
                  Rename to "Close" to read more naturally as
                  "I'm done here" instead of "abort". */}
              {matchPreset(from, to) ? 'Close' : 'Cancel'}
            </Button>
            {/* Apply-custom-range only shows when the user is actually
                editing the custom range (no preset matched). When a
                preset is the active mode, the section is collapsed and
                this button would be a confusing dead-end. */}
            {matchPreset(from, to) ? null : (
              <Button onClick={applyCustom} disabled={!customFrom && !customTo}>
                Apply custom range
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** ISO → YYYY-MM-DD for the native <input type="date">. Returns empty on
 *  missing input so the field renders blank. */
function toDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Format as local YYYY-MM-DD so the visible value matches the user's
  // wall clock (not UTC).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
